import * as XLSX from 'xlsx';

import {
  AccountingEntry,
  AccountingEntryClass,
  AccountingEntryLine,
  AccountingNode,
  AccountingPeriod,
  CostCenter,
} from '../../../../types';
import { ERROR_MESSAGES, EXCEL_COLUMN_MAP, VALIDATION_CONFIG } from './const';
import {
  ExcelRow,
  ProcessedEntriesResult,
  ProcessedEntry,
  ResolverContext,
  TableEntry,
  ValidationContext,
} from './types';

// Date Utilities

export const normalizeExcelDate = (
  date: string | number | undefined,
): string => {
  if (!date) {
    return new Date().toISOString().split('T')[0];
  }

  if (typeof date === 'number') {
    const excelEpoch = new Date(
      VALIDATION_CONFIG.EXCEL_EPOCH_YEAR,
      VALIDATION_CONFIG.EXCEL_EPOCH_MONTH,
      VALIDATION_CONFIG.EXCEL_EPOCH_DAY,
    );
    return new Date(
      excelEpoch.getTime() + date * VALIDATION_CONFIG.MILLISECONDS_PER_DAY,
    )
      .toISOString()
      .split('T')[0];
  }

  if (typeof date === 'string') {
    const parsedDate = new Date(date);
    if (!isNaN(parsedDate.getTime())) {
      return parsedDate.toISOString().split('T')[0];
    }
  }

  return new Date().toISOString().split('T')[0];
};

export const prepareDateForAPI = (dateString: string): Date => {
  const date = new Date(dateString);
  date.setUTCHours(VALIDATION_CONFIG.UTC_HOURS_FOR_API, 0, 0, 0);
  return date;
};

// Resolvers

const createNodeMap = (
  nodes: AccountingNode[],
): Map<string, AccountingNode> => {
  const map = new Map<string, AccountingNode>();
  nodes.forEach((node) => {
    if (node.accountCode) {
      map.set(node.accountCode.toLowerCase(), node);
    }
    map.set(node.key.toLowerCase(), node);
    map.set(String(node.id), node);
  });
  return map;
};

const createCostCenterMap = (
  costCenters: CostCenter[],
): Map<string, CostCenter> => {
  const map = new Map<string, CostCenter>();
  costCenters.forEach((cc) => {
    map.set(cc.name.toLowerCase(), cc);
    map.set(String(cc.id), cc);
  });
  return map;
};

const createEntryClassMap = (
  entryClasses: AccountingEntryClass[],
): Map<string, AccountingEntryClass> => {
  const map = new Map<string, AccountingEntryClass>();
  entryClasses.forEach((ec) => {
    map.set(ec.name.toLowerCase(), ec);
    map.set(ec.id.toLowerCase(), ec);
  });
  return map;
};

export const createResolvers = (context: ResolverContext) => {
  const nodeMap = createNodeMap(context.nodes);
  const costCenterMap = createCostCenterMap(context.costCenters);
  const entryClassMap = createEntryClassMap(context.entryClasses);

  const resolveNodeId = (nodeKey: string): number | null => {
    if (!nodeKey) return null;
    const node =
      nodeMap.get(nodeKey.toLowerCase()) || nodeMap.get(String(nodeKey));
    return node?.id || null;
  };

  const resolveCostCenterId = (costCenterName: string): number | null => {
    if (!costCenterName) return null;
    const costCenter =
      costCenterMap.get(costCenterName.toLowerCase()) ||
      costCenterMap.get(String(costCenterName));
    return costCenter?.id || null;
  };

  const resolveClassId = (className: string): string | null => {
    if (!className) return null;
    const entryClass =
      entryClassMap.get(className.toLowerCase()) ||
      entryClassMap.get(String(className));
    return entryClass?.id || null;
  };

  return { resolveNodeId, resolveCostCenterId, resolveClassId };
};

// Excel Parser

export const parseExcelFile = (file: File): Promise<ExcelRow[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const json = XLSX.utils.sheet_to_json(worksheet, {
          defval: '',
          raw: false,
        });

        const parsedData = (json as Record<string, unknown>[]).map(
          (row: Record<string, unknown>, index: number) => {
            const entryId = row[EXCEL_COLUMN_MAP.ENTRY_ID];
            const entryName = String(row[EXCEL_COLUMN_MAP.ENTRY_NAME] || '');
            const date = String(row[EXCEL_COLUMN_MAP.DATE] || '');
            const classId = String(row[EXCEL_COLUMN_MAP.CLASS] || '');
            const status =
              String(row[EXCEL_COLUMN_MAP.STATUS]).toLowerCase() === 'si'
                ? 'PENDING'
                : 'CLOSED';
            const nodeKey = String(row[EXCEL_COLUMN_MAP.NODE_KEY] || '');
            const debit =
              parseFloat(
                String(row[EXCEL_COLUMN_MAP.DEBIT] || 0).replace(
                  /[^0-9.-]/g,
                  '',
                ),
              ) || 0;
            const credit =
              parseFloat(
                String(row[EXCEL_COLUMN_MAP.CREDIT] || 0).replace(
                  /[^0-9.-]/g,
                  '',
                ),
              ) || 0;
            const documentKey = String(
              row[EXCEL_COLUMN_MAP.DOCUMENT_KEY] || '',
            );
            const businessIdentifier = String(
              row[EXCEL_COLUMN_MAP.BUSINESS_IDENTIFIER] || '',
            );
            const costCenterName = String(
              row[EXCEL_COLUMN_MAP.COST_CENTER] || '',
            );
            const description = String(row[EXCEL_COLUMN_MAP.DESCRIPTION] || '');

            return {
              entryId: entryId ? String(entryId) : undefined,
              entryName,
              date,
              classId,
              typeId: VALIDATION_CONFIG.DEFAULT_ENTRY_TYPE,
              status,
              nodeKey,
              debit,
              credit,
              documentKey,
              businessIdentifier,
              costCenterName,
              description,
              rowNumber: index + VALIDATION_CONFIG.EXCEL_HEADER_ROW_OFFSET,
            };
          },
        );

        resolve(parsedData);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

// Validation

const validateEntryName = (name: string | undefined): string | null => {
  if (!name || name.trim() === '') {
    return ERROR_MESSAGES.ENTRY_NAME_REQUIRED;
  }
  return null;
};

const validateEntryDate = (
  date: string | undefined,
  accountingPeriods: AccountingPeriod[],
): string | null => {
  if (!date) {
    return ERROR_MESSAGES.DATE_REQUIRED;
  }

  const entryDate = new Date(date);
  if (isNaN(entryDate.getTime())) {
    return ERROR_MESSAGES.DATE_INVALID;
  }

  const period = accountingPeriods.find((p) => {
    const startDate = new Date(p.startDate);
    const endDate = new Date(p.endDate);
    return entryDate >= startDate && entryDate <= endDate;
  });

  if (!period) {
    return ERROR_MESSAGES.DATE_NOT_IN_PERIOD;
  }

  if (period.status === 'CLOSED') {
    return ERROR_MESSAGES.PERIOD_CLOSED(period.label);
  }

  return null;
};

const validateEntryBalance = (lines: AccountingEntryLine[]): string | null => {
  const totalDebits = lines.reduce((sum, line) => sum + line.debit, 0);
  const totalCredits = lines.reduce((sum, line) => sum + line.credit, 0);

  if (
    Math.abs(totalDebits - totalCredits) > VALIDATION_CONFIG.BALANCE_TOLERANCE
  ) {
    const diff = totalDebits - totalCredits;
    return ERROR_MESSAGES.DEBITS_CREDITS_NOT_EQUAL(Math.abs(diff));
  }

  return null;
};

const validateEntryLines = (
  lines: AccountingEntryLine[],
  nodes: AccountingNode[],
): string[] => {
  const errors: string[] = [];

  if (lines.length === 0) {
    errors.push(ERROR_MESSAGES.NO_LINES);
    return errors;
  }

  const nodeMap = new Map<number, AccountingNode>();
  nodes.forEach((node) => {
    nodeMap.set(node.id, node);
  });

  lines.forEach((line, index) => {
    const lineNumber = index + 1;

    if (!line.nodeId) {
      const nodeKey = line.node?.key || '(vacío)';
      errors.push(ERROR_MESSAGES.NODE_NOT_FOUND(lineNumber, nodeKey));
      return;
    }

    const node = nodeMap.get(line.nodeId);
    if (!node) {
      errors.push(ERROR_MESSAGES.NODE_INVALID(lineNumber));
      return;
    }

    if (node.costCenterRequired && !line.costCenterId) {
      errors.push(ERROR_MESSAGES.COST_CENTER_REQUIRED(lineNumber, node.key));
    }

    if (node.businessIdentifierRequired && !line.businessIdentifier) {
      errors.push(
        ERROR_MESSAGES.BUSINESS_IDENTIFIER_REQUIRED(lineNumber, node.key),
      );
    }

    if (line.debit === 0 && line.credit === 0) {
      errors.push(ERROR_MESSAGES.ZERO_DEBIT_CREDIT(lineNumber));
    }

    if (line.debit < 0 || line.credit < 0) {
      errors.push(ERROR_MESSAGES.NEGATIVE_VALUES(lineNumber));
    }
  });

  return errors;
};

export const validateEntry = (
  entry: AccountingEntry,
  context: ValidationContext,
): string[] => {
  const errors: string[] = [];

  const nameError = validateEntryName(entry.name);
  if (nameError) errors.push(nameError);

  const dateError = validateEntryDate(entry.date, context.accountingPeriods);
  if (dateError) errors.push(dateError);

  const balanceError = validateEntryBalance(entry.lines);
  if (balanceError) errors.push(balanceError);

  const lineErrors = validateEntryLines(entry.lines, context.nodes);
  errors.push(...lineErrors);

  return errors;
};

// Entry Processing

const groupRowsByEntry = (excelData: ExcelRow[]): Map<string, ExcelRow[]> => {
  const entriesMap = new Map<string, ExcelRow[]>();

  excelData.forEach((row) => {
    const entryKey = row.entryId
      ? String(row.entryId)
      : row.entryName || 'Sin nombre';

    if (!entriesMap.has(entryKey)) {
      entriesMap.set(entryKey, []);
    }
    entriesMap.get(entryKey)!.push(row);
  });

  return entriesMap;
};

const buildEntryLines = (
  rows: ExcelRow[],
  resolvers: ReturnType<typeof createResolvers>,
): AccountingEntryLine[] => {
  const lines: AccountingEntryLine[] = [];

  rows.forEach((row) => {
    const nodeKey = row.nodeKey || '';
    const nodeId = resolvers.resolveNodeId(nodeKey);
    const costCenterId = row.costCenterName
      ? resolvers.resolveCostCenterId(row.costCenterName)
      : null;

    lines.push({
      nodeId: nodeId || null,
      debit: row.debit || 0,
      credit: row.credit || 0,
      businessIdentifier: row.businessIdentifier || null,
      costCenterId,
      documentKey: row.documentKey || undefined,
      description: row.description || '',
      node: nodeId
        ? undefined
        : {
            key: nodeKey,
            id: 0,
            costCenterRequired: false,
            documentKeyRequired: false,
            businessIdentifierRequired: false,
          },
    });
  });

  return lines;
};

const buildEntry = (
  rows: ExcelRow[],
  resolvers: ReturnType<typeof createResolvers>,
  companyId: number,
): AccountingEntry => {
  const firstRow = rows[0];
  const entryDate = normalizeExcelDate(firstRow.date);
  const classId = resolvers.resolveClassId(firstRow.classId || '') || '';
  const typeId = firstRow.typeId || VALIDATION_CONFIG.DEFAULT_ENTRY_TYPE;
  const status = (firstRow.status || VALIDATION_CONFIG.DEFAULT_ENTRY_STATUS) as
    | 'CLOSED'
    | 'PENDING';

  const entryName =
    firstRow.entryName ||
    (firstRow.entryId ? `Asiento ${firstRow.entryId}` : 'Sin nombre');

  const lines = buildEntryLines(rows, resolvers);

  return {
    name: entryName,
    date: entryDate,
    classId,
    typeId,
    status,
    companyId,
    lines,
  };
};

export const processEntries = (
  excelData: ExcelRow[],
  resolverContext: ResolverContext,
  validationContext: ValidationContext,
  companyId: number,
): ProcessedEntriesResult => {
  const valid: ProcessedEntry[] = [];
  const invalid: ProcessedEntry[] = [];
  const resolvers = createResolvers(resolverContext);
  const entriesMap = groupRowsByEntry(excelData);

  entriesMap.forEach((rows) => {
    if (rows.length === 0) return;

    const entry = buildEntry(rows, resolvers, companyId);
    const errors = validateEntry(entry, validationContext);

    const processedEntry: ProcessedEntry = {
      entry,
      errors,
      rowNumbers: rows.map((r) => r.rowNumber || 0),
    };

    if (errors.length === 0) {
      valid.push(processedEntry);
    } else {
      invalid.push(processedEntry);
    }
  });

  return { valid, invalid };
};

// Formatting

export const formatValidEntriesForTable = (
  validEntries: ProcessedEntry[],
): TableEntry[] => {
  return validEntries.map((processedEntry, index) => {
    const totalDebit = processedEntry.entry.lines.reduce(
      (sum, line) => sum + line.debit,
      0,
    );
    const totalCredit = processedEntry.entry.lines.reduce(
      (sum, line) => sum + line.credit,
      0,
    );

    return {
      id: `entry-${index}`,
      name: processedEntry.entry.name,
      date: processedEntry.entry.date,
      linesCount: processedEntry.entry.lines.length,
      totalDebit,
      totalCredit,
    };
  });
};

export const formatInvalidEntriesForTable = (
  invalidEntries: ProcessedEntry[],
): TableEntry[] => {
  return invalidEntries.map((processedEntry) => ({
    id: processedEntry.entry.name,
    name: processedEntry.entry.name,
    date: processedEntry.entry.date,
    linesCount: processedEntry.entry.lines.length,
    errors: processedEntry.errors.join('; '),
  }));
};

export const calculateTotalLines = (validEntries: ProcessedEntry[]): number => {
  return validEntries.reduce((sum, entry) => sum + entry.entry.lines.length, 0);
};

// Error Handling

export interface ErrorExtractionResult {
  message: string;
  missingRuts?: string[];
}

const extractRutsFromMessage = (message: string): string[] => {
  const rutPattern = /Estas empresas no existen:\s*(.+)/i;
  const match = message.match(rutPattern);

  if (!match?.[1]) {
    return [];
  }

  // Extraer los RUTs separados por comas y limpiar espacios
  const rutsString = match[1].trim();
  const ruts = rutsString
    .split(',')
    .map((rut) => rut.trim())
    .filter((rut) => rut.length > 0);

  return ruts;
};

export const extractErrorMessage = (
  error: unknown,
): ErrorExtractionResult | undefined => {
  let message: string | undefined;
  let missingRuts: string[] | undefined;

  if (error && typeof error === 'object') {
    if (
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'data' in error.response &&
      error.response.data
    ) {
      const responseData = error.response.data;

      if (typeof responseData === 'object') {
        // Buscar en message
        if (
          'message' in responseData &&
          typeof responseData.message === 'string'
        ) {
          message = responseData.message;
        } else if (
          'error' in responseData &&
          typeof responseData.error === 'string'
        ) {
          message = responseData.error;
        } else {
          const dataRecord = responseData as Record<string, unknown>;
          for (const key in dataRecord) {
            const value = dataRecord[key];
            if (typeof value === 'string' && value.length > 0) {
              message = value;
              break;
            }
          }
        }

        if (
          'missingRuts' in responseData &&
          Array.isArray(responseData.missingRuts)
        ) {
          missingRuts = responseData.missingRuts.filter(
            (rut): rut is string => typeof rut === 'string',
          );
        }
      } else if (typeof responseData === 'string') {
        message = responseData;
      }
    }

    if (!message && 'message' in error && typeof error.message === 'string') {
      message = error.message;
    }

    if (!message && 'stack' in error && typeof error.stack === 'string') {
      message = error.stack;
    }
  } else if (typeof error === 'string') {
    message = error;
  }

  if (!message) {
    return undefined;
  }

  const messageString = String(message);

  // Extraer RUTs si el mensaje indica que las empresas no existen
  if (!missingRuts && messageString.includes('Estas empresas no existen')) {
    missingRuts = extractRutsFromMessage(messageString);
  }

  // Si encontramos RUTs faltantes, usar el mensaje de ERROR_MESSAGES
  let finalMessage = messageString;
  if (missingRuts && missingRuts.length > 0) {
    finalMessage = ERROR_MESSAGES.BUSINESS_NOT_FOUND(missingRuts);
  }

  const result: ErrorExtractionResult = { message: finalMessage };
  if (missingRuts && missingRuts.length > 0) {
    result.missingRuts = missingRuts;
  }
  return result;
};

export const prepareEntriesForImport = (
  selectedEntries: string[],
  validEntries: ProcessedEntry[],
): AccountingEntry[] => {
  return selectedEntries
    .map((id) => {
      const index = parseInt(id.replace('entry-', ''), 10);
      return validEntries[index];
    })
    .filter(Boolean)
    .map((processedEntry) => {
      const dateObj = prepareDateForAPI(processedEntry.entry.date);
      return {
        ...processedEntry.entry,
        date: dateObj.toISOString(),
        lines: processedEntry.entry.lines.map((line) => ({
          ...line,
          description: line.description || undefined,
        })),
      };
    });
};
